import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import { createPortal } from "react-dom";
import { getGroup, postGroupSettle } from "../services/AnimalSplitCore";
import { connectGroupSocket } from "../services/SocketService";
import ContributorList from "./ContributorList";
import TransactionList from "./TransactionList";
import GameCenterModal from "./GameCenterModal";
import Navbar from "../components/Navbar";
import EditGroup from "./EditGroup";
import ShareGroup from "./ShareGroup";
import { toggleModal } from "../utils/utils";
import { getStableAnimalEmoji } from "../utils/utils";
import { setShareMeta } from "../utils/shareMeta";
import Prompt from "../components/Prompt";
import GroupFirstTimeInstructionsModal from "../components/GroupFirstTimeInstructionsModal";
import InfoModal from "../components/InfoModal";
import SettleConfirmModal from "../components/SettleConfirmModal";
import { MODAL_ID } from "../constants";
import { useTranslation } from "react-i18next";
import { useScene } from "../components/scene/SceneContext";
import Scene from "../components/scene/Scene";
import GroupHero from "../components/group/GroupHero";
import AiChatDrawer from "../components/chat/AiChatDrawer";
import "./ModalTheme.css";
import "./GroupScene.css";

export default function Group(props) {
  const { setIsLoading } = props;
  const { t, i18n } = useTranslation();
  const { variant } = useScene();
  const { group_id: groupId } = useParams();
  const history = useHistory();
  const [groupName, setGroupName] = useState("");
  const [contributors, setContributors] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [settlements, setSettlements] = useState({});
  const [netTotals, setNetTotals] = useState(null);
  const [settlementType, setSettlementType] = useState("");
  const [isSettled, setIsSettled] = useState(false);
  const [isSettling, setIsSettling] = useState(false);
  const [settlementInteractionBlockedUntil, setSettlementInteractionBlockedUntil] =
    useState(0);
  const [contributorIdDict, setContributorIdDict] = useState({});
  const [sortOrder, setSortOrder] = useState("");
  const [getGroupError, setGetGroupError] = useState(null);
  const editGroupModalId = "edit-group-modal";
  const shareGroupModalId = "share-group-modal";
  const islandEntryModalId = "group-island-entry-modal";
  const gameCenterModalId = "group-game-center-modal";
  const settleConfirmModalId = "group-settle-confirm-modal";
  const [showFirstTimeInstructions, setShowFirstTimeInstructions] = useState(false);

  const heroAnnouncementKey = useMemo(() => {
    const id = groupId || "unknown";
    return `animalSplit:group:heroAnnouncements:v1:${id}`;
  }, [groupId]);

  // Collapsed by default; users can expand and we persist the choice per-group.
  const [showHeroAnnouncements, setShowHeroAnnouncements] = useState(false);

  useEffect(() => {
    try {
      const v = localStorage.getItem(heroAnnouncementKey);
      if (v === "0") setShowHeroAnnouncements(false);
      if (v === "1") setShowHeroAnnouncements(true);
    } catch (e) {
      // ignore localStorage failures
    }
  }, [heroAnnouncementKey]);

  const toggleHeroAnnouncements = useCallback(() => {
    setShowHeroAnnouncements((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(heroAnnouncementKey, next ? "1" : "0");
      } catch (e) {
        // ignore localStorage failures
      }
      return next;
    });
  }, [heroAnnouncementKey]);

  const firstTimeInstructionsKey = useMemo(() => {
    const id = groupId || "unknown";
    return `animalSplit:group:firstTimeInstructions:v1:${id}`;
  }, [groupId]);

  const heroAnimal2 = useMemo(() => {
    return getStableAnimalEmoji(`friend:${groupId || groupName || "animalsplit"}`);
  }, [groupId, groupName]);

  useEffect(() => {
    if (getGroupError) {
      console.error(getGroupError);
      throw getGroupError;
    }
  }, [getGroupError]);

  const renderGroup = useCallback(
    (payload) => {
      const {
        name,
        contributors: contributors_data,
        transactions: transactions_data,
        settlements: settlements_data,
        netTotals: net_totals,
        settlementType: settlement_type_data,
        sortOrder: sort_order_data,
        isSettled: is_settled,
        lang,
      } = payload;

      setGroupName(name);
      setContributors(contributors_data);
      setTransactions(transactions_data);
      setSettlements(settlements_data);
      setNetTotals(net_totals || null);
      // Be tolerant: some older groups may not have settlementType persisted.
      // Default to "merged" so settlement rows render.
      setSettlementType(settlement_type_data || "merged");
      setSortOrder(sort_order_data);
      setIsSettled(Boolean(is_settled));
      i18n.changeLanguage(lang);

      const contributorIdDict = {};
      contributors_data.forEach((c) => (contributorIdDict[c.id] = c));
      setContributorIdDict(contributorIdDict);
    },
    [i18n]
  );

  const closeModalById = useCallback((modalId) => {
    const el = document.getElementById(modalId);
    if (!el || !window?.bootstrap?.Modal) return;
    try {
      const modal =
        window.bootstrap.Modal.getInstance(el) ||
        window.bootstrap.Modal.getOrCreateInstance(el);
      modal.hide();
    } catch (e) {
      // best-effort cleanup
      document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
      document.body.classList.remove("modal-open");
      document.body.style.removeProperty("padding-right");
      document.body.style.removeProperty("overflow");
    }
  }, []);

  const handleConfirmSettle = useCallback(async () => {
    if (!groupId || isSettled || isSettling) return;
    setIsSettling(true);
    setIsLoading(true);
    try {
      const { payload } = await postGroupSettle(groupId);
      renderGroup(payload);
      // Prevent a "tap-through" opening a settlement item modal right after settle.
      setSettlementInteractionBlockedUntil(Date.now() + 1000);
      closeModalById(settleConfirmModalId);
    } catch (e) {
      console.error(e);
      toggleModal(MODAL_ID.GENERIC_ERROR);
    } finally {
      setIsSettling(false);
      setIsLoading(false);
    }
  }, [
    groupId,
    isSettled,
    isSettling,
    setIsLoading,
    renderGroup,
    setSettlementInteractionBlockedUntil,
    closeModalById,
    settleConfirmModalId,
  ]);

  const refreshGroup = useCallback(
    (groupId) => {
      setIsLoading(true);
      getGroup(groupId)
        .then(({ payload }) => {
          setIsLoading(false);
          renderGroup(payload);
        })
        .catch((error) => {
          setIsLoading(false);
          setGetGroupError(error);
        });
    },
    [renderGroup, setIsLoading]
  );

  const handleSettingsBtnOnClick = useCallback(() => {
    toggleModal(editGroupModalId);
  }, [editGroupModalId]);

  const handleShareBtnOnClick = useCallback(() => {
    toggleModal(shareGroupModalId);
  }, [shareGroupModalId]);

  const handleGameCenterBtnOnClick = useCallback(() => {
    toggleModal(gameCenterModalId);
  }, [gameCenterModalId]);

  const islandHref = useMemo(() => {
    if (!groupId) return `${process.env.PUBLIC_URL}/`;
    return `${process.env.PUBLIC_URL}/group/${groupId}/island`;
  }, [groupId]);

  const handleVisitIsland = useCallback(() => {
    // Close the modal first, and navigate only after Bootstrap finishes removing the backdrop.
    const modalEl = document.getElementById(islandEntryModalId);
    if (!modalEl || !window?.bootstrap?.Modal) {
      history.push(islandHref);
      return;
    }

    const navigate = () => history.push(islandHref);

    try {
      modalEl.addEventListener("hidden.bs.modal", navigate, { once: true });
      const modal =
        window.bootstrap.Modal.getInstance(modalEl) ||
        window.bootstrap.Modal.getOrCreateInstance(modalEl);
      modal.hide();
    } catch (e) {
      // Fallback: best-effort cleanup then navigate
      document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
      document.body.classList.remove("modal-open");
      document.body.style.removeProperty("padding-right");
      document.body.style.removeProperty("overflow");
      navigate();
    }
  }, [history, islandEntryModalId, islandHref]);


  useEffect(() => {
    refreshGroup(groupId);
  }, [refreshGroup, groupId]);

  // Realtime: subscribe to group updates so other clients' edits update this page without refresh
  useEffect(() => {
    if (!groupId) return;
    const disconnect = connectGroupSocket(groupId, (latestGroup) => {
      renderGroup(latestGroup);
    });
    return () => {
      if (disconnect) disconnect();
    };
  }, [groupId, renderGroup]);

  // First-time instructions (once per group per browser)
  useEffect(() => {
    if (!groupId) return;
    try {
      const seen = localStorage.getItem(firstTimeInstructionsKey);
      if (!seen) setShowFirstTimeInstructions(true);
    } catch (e) {
      // ignore localStorage failures (private mode, etc.)
    }
  }, [groupId, firstTimeInstructionsKey]);

  // Lock background scroll while the modal is open (matches other overlays in the app)
  useEffect(() => {
    if (!showFirstTimeInstructions) return;
    // Mobile UX: don't lock body scroll. iOS Safari in particular can feel "stuck" when
    // an overlay is open + body scrolling is disabled, and users report the page as
    // "not scrollable". Keep the lock on larger screens.
    const shouldLockBodyScroll =
      typeof window !== "undefined" ? window.innerWidth >= 768 : true;
    if (!shouldLockBodyScroll) return;

    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, [showFirstTimeInstructions]);

  const dismissFirstTimeInstructions = useCallback(() => {
    try {
      localStorage.setItem(firstTimeInstructionsKey, "1");
    } catch (e) {
      // ignore localStorage failures (private mode, etc.)
    }
    setShowFirstTimeInstructions(false);
  }, [firstTimeInstructionsKey]);

  useEffect(() => {
    const title = groupName?.trim() ? groupName.trim() : "AnimalSplit";
    const description =
      t("share.animalsplit_card_description") ||
      "AnimalSplit helps friends split group expenses easily — simple, fair, and fun.";
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const image = `${origin}${process.env.PUBLIC_URL || ""}/icon.png`;
    const url = typeof window !== "undefined" ? window.location.href : "";
    setShareMeta({ title, description, image, url });
  }, [groupName, t]);

  return (
    <div className={`group-page group-page--${variant} as-theme`}>
      <Scene />
      <div className="group-page-content">
        <Navbar
          shareBtnIconClassName="bi-share-fill"
          settingsBtnIconClassName="bi-gear-fill"
          showActionBtn={true}
          handleSettingsBtnOnClick={handleSettingsBtnOnClick}
          handleShareBtnOnClick={handleShareBtnOnClick}
          rightSlot={
            <>
              <button
                type="button"
                className="btn btn-sm as-btn-soft"
                aria-label={t("group.island") || "Island"}
                title={t("group.island") || "Island"}
                data-bs-toggle="modal"
                data-bs-target={`#${islandEntryModalId}`}
              >
                <i className="bi bi-map" />
              </button>
              <button
                type="button"
                className="btn btn-sm as-btn-soft"
                onClick={handleGameCenterBtnOnClick}
                aria-label={t("games.group_card.title") || "Games"}
                title={t("games.group_card.title") || "Games"}
              >
                <i className="bi bi-controller" />
              </button>
            </>
          }
        />
        <div className="container-xl">
          {/* Hero aligns with the two-column grid (5 + 5 = 10 on xl) */}
          <div className="row gx-4 justify-content-center">
            <div className="col-12 col-xl-10">
              <GroupHero
                title={groupName || t("group.loading_title")}
                subtitle={
                  <>
                    <div className="group-hero-subtitle-row">
                      <div className="group-hero-subtitle-text">
                        {t("group.hero_subtitle")}
                        {" • "}
                        <span className="group-hero-subtitle-cta">
                          {t("group.hero_subtitle_cta") || "💡 Click to see what's new"}
                          <button
                            type="button"
                            className="group-hero-announce-toggle"
                            onClick={toggleHeroAnnouncements}
                            aria-label={
                              showHeroAnnouncements
                                ? (t("group.hide_updates") || "Hide updates")
                                : (t("group.show_updates") || "Show updates")
                            }
                            title={
                              showHeroAnnouncements
                                ? (t("group.hide_updates") || "Hide updates")
                                : (t("group.show_updates") || "Show updates")
                            }
                          >
                            <i className={`bi ${showHeroAnnouncements ? "bi-chevron-up" : "bi-chevron-down"}`} />
                          </button>
                        </span>
                      </div>
                    </div>
                    {showHeroAnnouncements ? (
                      <div className="group-hero-announcements">
                        <div className="group-hero-announcement">
                          {t("group.hero_subtitle_island_cta") ||
                            "🏝️ New: Animal Island is here — try Island mode!"}
                        </div>
                        <div className="group-hero-announcement">
                          {t("group.share_settlements_announcement") ||
                            "🧾 New: Share settlements (Share → Share settlements)"}
                        </div>
                      </div>
                    ) : null}
                  </>
                }
                showMascot={false}
                actions={
                  <>
                    {!isSettled ? (
                      <button
                        type="button"
                        className="btn btn-primary as-btn-primary-gradient text-nowrap"
                        aria-label={t("group.settle") || "Settle"}
                        title={t("group.settle") || "Settle"}
                        data-bs-toggle="modal"
                        data-bs-target={`#${settleConfirmModalId}`}
                        disabled={!groupId || isSettling}
                        style={{ minWidth: 80 }}
                      >
                        <span className="fw-semibold">{t("group.settle") || "Settle"}</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm as-btn-soft text-nowrap"
                        aria-label={t("group.settled") || "Settled"}
                        title={t("group.settled") || "Settled"}
                        disabled
                        style={{ minWidth: 80 }}
                      >
                        <i className="bi bi-lock-fill me-1" />
                        <span className="d-inline d-sm-none small fw-semibold">
                          {t("group.settled") || "Settled"}
                        </span>
                        <span className="d-none d-sm-inline">{t("group.settled") || "Settled"}</span>
                      </button>
                    )}
                  </>
                }
                meadowIcons={["🌳", "🌿", "🌱", "🐾", heroAnimal2, "🌼", "🌿", "🌱"]}
              />
            </div>
          </div>
          <div className="row gx-4 justify-content-center">
            <div className="col-md-6 col-lg-6 col-xl-5 mt-3 mb-4">
              <TransactionList
                groupId={groupId}
                contributors={contributors}
                transactions={transactions}
                renderGroup={renderGroup}
                sortOrder={sortOrder}
                setIsLoading={setIsLoading}
                isSettled={isSettled}
              />
            </div>

            <div className="col-md-6 col-lg-6 col-xl-5 mt-3">
              <ContributorList
                groupId={groupId}
                groupName={groupName}
                contributors={contributors}
                renderGroup={renderGroup}
                settlements={settlements}
                netTotals={netTotals}
                contributorIdDict={contributorIdDict}
                settlementType={settlementType}
                setIsLoading={setIsLoading}
                isSettled={isSettled}
                settlementInteractionBlockedUntil={settlementInteractionBlockedUntil}
              />
            </div>
          </div>
        </div>
      </div>
      <AiChatDrawer groupId={groupId} groupName={groupName} />
      <GroupFirstTimeInstructionsModal
        open={showFirstTimeInstructions}
        onDismiss={dismissFirstTimeInstructions}
        groupId={groupId}
        groupName={groupName}
      />
      <EditGroup
        modalId={editGroupModalId}
        groupId={groupId}
        renderGroup={renderGroup}
        groupName={groupName}
        settlementType={settlementType}
        setIsLoading={setIsLoading}
        isSettled={isSettled}
      />
      {typeof document !== "undefined"
        ? createPortal(
            <GameCenterModal
              modalId={gameCenterModalId}
              groupId={groupId}
              transactions={transactions}
              setIsLoading={setIsLoading}
            />,
            document.body
          )
        : null}
      <ShareGroup
        modalId={shareGroupModalId}
        groupId={groupId}
        groupName={groupName}
        isSettled={isSettled}
        contributors={contributors}
        settlements={settlements}
        settlementType={settlementType}
        contributorIdDict={contributorIdDict}
        sceneVariant={variant}
      />
      <InfoModal
        modalId={islandEntryModalId}
        icon="🏝️"
        title={t("group.island_entry_title") || "Animal Island"}
        body={
          <div>
            <p className="mb-2">
              {t("group.island_entry_intro") ||
                "Build your own island with your group during the trip."}
            </p>
            <p className="mb-2">
              {t("group.island_entry_how") ||
                "Each transaction becomes a building, and each participant becomes an animal."}
            </p>
            <p className="mb-2">
              {t("group.island_entry_features") ||
                "All features can be done in island mode — just in a more playful way:"}
            </p>
            <ul className="mb-3">
              <li>{t("group.island_entry_map_construction") || "Construction Center = create transaction"}</li>
              <li>{t("group.island_entry_map_harbor") || "Harbor = new participant"}</li>
              <li>{t("group.island_entry_map_visitor_center") || "Visitor Center = check settlements"}</li>
              <li>{t("group.island_entry_map_animal") || "Animal = edit participant"}</li>
              <li>{t("group.island_entry_map_building") || "Building = edit transaction"}</li>
            </ul>
            <button
              type="button"
              className="btn as-btn-primary-gradient w-100"
              onClick={handleVisitIsland}
            >
              {t("group.island_entry_cta") || "Visit your island!"}
            </button>
          </div>
        }
      />
      <SettleConfirmModal
        modalId={settleConfirmModalId}
        groupId={groupId}
        groupName={groupName}
        contributors={contributors}
        cancelText={t("general.cancel") || "Cancel"}
        confirmText={
          isSettling
            ? (t("group.settling") || "Settling…")
            : (t("group.settle_confirm_cta") || "Settle now")
        }
        confirmVariantClassName="as-btn-primary-gradient"
        onConfirm={handleConfirmSettle}
        confirmDisabled={!groupId || isSettled || isSettling}
        cancelDisabled={isSettling}
      />
      <Prompt
        modalId={MODAL_ID.CONNECTION_ERROR}
        title="Oops"
        btnTitle="Dismiss"
      >
        <p>There is a connection error</p>
      </Prompt>
    </div>
  );
}
